import { useState, useEffect, useCallback, useMemo } from "react";
import { Form, Button, Spinner, Modal } from "react-bootstrap";
import Swal from "sweetalert2";
import DisentimientoModal from "./DisentimientoModal";
import {
  getDisentimientosBac,
  getDisentimientoBac,
  generarFichaDisentimientoBac,
  eliminarDisentimientoBac,
  urlFichaDisentimiento,
} from "../api/disentimientoAPI";
import {
  leerDisentimientosPendientesBac,
  cachearDisentimientosBac,
  leerDisentimientosCacheBac,
} from "./disentimientoSync";
import { useOnlineStatus } from "../../hooks/useSync";
import { formatearFecha, formatearEdad, estadoFicha } from "./disentimientoComun";

// ════════════════════════════════════════════════════════════════════
// Pestaña "Disentimiento" de BAC — lista de constancias + registro.
// Funciona sin señal: muestra la última lista guardada en el dispositivo
// y, encima, las constancias que aún no se han podido subir.
// ════════════════════════════════════════════════════════════════════

export default function TablaDisentimiento({ esAdmin }) {
  const online = useOnlineStatus();
  const [registros, setRegistros] = useState([]);     // del servidor (o de la caché)
  const [pendientes, setPendientes] = useState([]);   // locales, sin subir
  const [origen, setOrigen] = useState(null);         // { tipo: 'servidor'|'dispositivo', cachedAt }
  const [cargando, setCargando] = useState(true);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [detalle, setDetalle] = useState(null);       // registro (servidor o local)
  const [ocupadoId, setOcupadoId] = useState(null);

  const [fMunicipio, setFMunicipio] = useState("");
  const [fDesde, setFDesde] = useState("");
  const [fHasta, setFHasta] = useState("");
  const [fBuscar, setFBuscar] = useState("");

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      setPendientes(await leerDisentimientosPendientesBac());
      if (navigator.onLine) {
        try {
          const res = await getDisentimientosBac();
          const lista = res.data || [];
          setRegistros(lista);
          setOrigen({ tipo: "servidor" });
          cachearDisentimientosBac(lista);
          return;
        } catch (e) {
          console.warn("⚠️ No se pudo traer la lista de disentimientos, se usa la del dispositivo:", e.message);
        }
      }
      const cache = await leerDisentimientosCacheBac();
      setRegistros(cache.registros);
      setOrigen({ tipo: "dispositivo", cachedAt: cache.cachedAt });
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    const recargar = () => cargar();
    window.addEventListener("disentimiento-actualizado", recargar);
    window.addEventListener("disentimiento-sync-completado", recargar);
    window.addEventListener("sync-completado", recargar);
    window.addEventListener("online", recargar);
    return () => {
      window.removeEventListener("disentimiento-actualizado", recargar);
      window.removeEventListener("disentimiento-sync-completado", recargar);
      window.removeEventListener("sync-completado", recargar);
      window.removeEventListener("online", recargar);
    };
  }, [cargar]);

  // Locales primero (marcados), luego los del servidor; un local que ya
  // existe en el servidor (mismo código) no se repite.
  const todos = useMemo(() => {
    const codigosServidor = new Set(registros.map(r => r.codigo));
    const locales = pendientes
      .filter(p => !codigosServidor.has(p.codigo))
      .map(p => ({ ...p, _local: true, _localId: p.id }));
    return [...locales, ...registros];
  }, [registros, pendientes]);

  const filtrados = useMemo(() => {
    const q = fBuscar.trim().toLowerCase();
    return todos.filter(r => {
      if (fMunicipio && r.municipio !== fMunicipio) return false;
      const fecha = String(r.fecha_atencion || "").slice(0, 10);
      if (fDesde && fecha < fDesde) return false;
      if (fHasta && fecha > fHasta) return false;
      if (!q) return true;
      return [r.menor_nombre, r.representante_nombre, r.menor_documento, r.representante_documento, r.codigo, r.comunidad]
        .some(v => String(v || "").toLowerCase().includes(q));
    });
  }, [todos, fMunicipio, fDesde, fHasta, fBuscar]);

  const resumen = useMemo(() => ({
    total: todos.length,
    porSincronizar: todos.filter(r => r._local).length,
    enviadas: todos.filter(r => !r._local && r.ficha_estado === "enviada").length,
    municipios: new Set(todos.map(r => r.municipio).filter(Boolean)).size,
  }), [todos]);

  // Opciones del filtro: los municipios/territorios que realmente tienen registros
  const municipiosConRegistros = useMemo(
    () => [...new Set(todos.map(r => r.municipio).filter(Boolean))].sort((a, b) => a.localeCompare(b, "es")),
    [todos]
  );

  const exigirConexion = (accion) => {
    if (navigator.onLine) return true;
    Swal.fire("Sin conexión", `Para ${accion} se necesita internet.`, "warning");
    return false;
  };

  const generarFicha = async (r) => {
    if (!exigirConexion("generar la ficha")) return;
    setOcupadoId(r.id);
    try {
      const res = await generarFichaDisentimientoBac(r.id);
      const enviada = res.data?.disentimiento?.ficha_estado === "enviada";
      Swal.fire({ icon: enviada ? "success" : "info", title: res.data?.message || "Ficha generada", timer: 2400, showConfirmButton: false });
      cargar();
    } catch (err) {
      Swal.fire("No se pudo generar la ficha", err.response?.data?.message || "Intenta de nuevo.", "error");
    } finally {
      setOcupadoId(null);
    }
  };

  const eliminar = async (r) => {
    // Solo administradores (el servidor también lo exige: soloAdminBac)
    if (!esAdmin) {
      Swal.fire("Acción no permitida", "Solo un administrador puede eliminar constancias.", "warning");
      return;
    }
    if (!exigirConexion("eliminar")) return;
    const ok = await Swal.fire({
      title: "¿Eliminar esta constancia?",
      html: `<div style="font-size:14px">${r.menor_nombre} — ${formatearFecha(r.fecha_atencion)}<br/><br/>Se borra el registro y su ficha en PDF. No se puede deshacer.</div>`,
      icon: "warning", showCancelButton: true, confirmButtonText: "Sí, eliminar", cancelButtonText: "Cancelar", confirmButtonColor: "#dc3545",
    });
    if (!ok.isConfirmed) return;
    setOcupadoId(r.id);
    try {
      await eliminarDisentimientoBac(r.id);
      Swal.fire({ icon: "success", title: "Constancia eliminada", timer: 1500, showConfirmButton: false });
      cargar();
    } catch (err) {
      Swal.fire("No se pudo eliminar", err.response?.data?.message || "Intenta de nuevo.", "error");
    } finally {
      setOcupadoId(null);
    }
  };

  const limpiarFiltros = () => { setFMunicipio(""); setFDesde(""); setFHasta(""); setFBuscar(""); };
  const hayFiltros = fMunicipio || fDesde || fHasta || fBuscar;

  return (
    <div className="tabla-disentimiento">
      <style>{`
        .tabla-disentimiento { text-align: left; }
        .dis-modal-content { padding: 0 !important; max-height: none !important; overflow: hidden !important; max-width: none !important; text-align: left; }
        @media (min-width: 576px) { .dis-modal-content { max-height: calc(100vh - 3.5rem) !important; } }
        .tabla-disentimiento .dis-stats { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; margin-bottom: 14px; }
        .tabla-disentimiento .dis-stat { background: #fff; border: 1px solid #DDE5E0; border-radius: 12px; padding: 12px 14px; }
        .tabla-disentimiento .dis-stat .n { font-size: 22px; font-weight: 800; color: #0E4F57; line-height: 1.1; }
        .tabla-disentimiento .dis-stat .l { font-size: 12px; color: #4C6360; font-weight: 600; }
        .tabla-disentimiento .dis-filtros { display: flex; gap: 10px; flex-wrap: wrap; align-items: flex-end; background: #fff; border: 1px solid #DDE5E0; border-radius: 12px; padding: 12px; margin-bottom: 14px; }
        .tabla-disentimiento .dis-filtros label { font-size: 11.5px; margin-bottom: 4px; color: #4C6360; font-weight: 600; }
        .tabla-disentimiento .dis-pill { font-size: 11.5px; font-weight: 700; padding: 3px 10px; border-radius: 20px; white-space: nowrap; display: inline-block; }
        .tabla-disentimiento table { width: 100%; border-collapse: collapse; font-size: 13.5px; }
        .tabla-disentimiento th { text-align: left; font-size: 11.5px; color: #83958F; padding: 11px 12px; white-space: nowrap; border-bottom: 1.5px solid #DDE5E0; font-weight: 700; }
        .tabla-disentimiento td { padding: 11px 12px; border-bottom: 1px solid #EEF1F0; vertical-align: middle; }
        .tabla-disentimiento tr.local td { background: #FFFCF2; }
        .tabla-disentimiento .sub { font-size: 12px; color: #83958F; }
        .tabla-disentimiento .dis-card { background: #fff; border: 1px solid #DDE5E0; border-radius: 12px; padding: 12px 14px; margin-bottom: 10px; }
        .tabla-disentimiento .dis-card.local { background: #FFFCF2; border-color: #F2DFA8; }
        @media (max-width: 767px) {
          .tabla-disentimiento .dis-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .tabla-disentimiento .dis-filtros > div { flex: 1 1 140px; }
        }
      `}</style>

      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <div>
          <h5 className="mb-0" style={{ color: "#0E4F57", fontWeight: 700 }}>Disentimiento PAI — constancias de vacunación no aceptada</h5>
          {origen?.tipo === "dispositivo" && (
            <div className="sub" style={{ marginTop: 2 }}>
              📡 Mostrando la lista guardada en este dispositivo{origen.cachedAt ? ` (actualizada el ${new Date(origen.cachedAt).toLocaleString("es-CO")})` : ""}.
            </div>
          )}
        </div>
        <Button onClick={() => setMostrarFormulario(true)} style={{ background: "#E8703A", border: "none", fontWeight: 700 }}>
          ＋ Nueva constancia
        </Button>
      </div>

      <div className="dis-stats">
        <div className="dis-stat"><div className="n">{resumen.total}</div><div className="l">Constancias</div></div>
        <div className="dis-stat"><div className="n" style={{ color: resumen.porSincronizar ? "#8A6100" : "#0E4F57" }}>{resumen.porSincronizar}</div><div className="l">Por sincronizar</div></div>
        <div className="dis-stat"><div className="n" style={{ color: "#2C6B2F" }}>{resumen.enviadas}</div><div className="l">Fichas enviadas por correo</div></div>
        <div className="dis-stat"><div className="n">{resumen.municipios}</div><div className="l">Municipios</div></div>
      </div>

      <div className="dis-filtros">
        <div>
          <Form.Label>Municipio</Form.Label>
          <Form.Select size="sm" value={fMunicipio} onChange={e => setFMunicipio(e.target.value)} style={{ minWidth: 160 }}>
            <option value="">Todos</option>
            {municipiosConRegistros.map(m => <option key={m} value={m}>{m}</option>)}
          </Form.Select>
        </div>
        <div>
          <Form.Label>Atención desde</Form.Label>
          <Form.Control size="sm" type="date" value={fDesde} onChange={e => setFDesde(e.target.value)} />
        </div>
        <div>
          <Form.Label>Atención hasta</Form.Label>
          <Form.Control size="sm" type="date" value={fHasta} onChange={e => setFHasta(e.target.value)} />
        </div>
        <div style={{ flexGrow: 1, minWidth: 180 }}>
          <Form.Label>Buscar</Form.Label>
          <Form.Control size="sm" value={fBuscar} onChange={e => setFBuscar(e.target.value)} placeholder="Nombre o documento del niño o del representante" />
        </div>
        {hayFiltros && (
          <Button size="sm" variant="link" onClick={limpiarFiltros} style={{ color: "#CC5A26", fontWeight: 700, textDecoration: "none" }}>
            Limpiar filtros
          </Button>
        )}
      </div>

      {cargando && todos.length === 0 ? (
        <div className="text-center p-4"><Spinner /></div>
      ) : filtrados.length === 0 ? (
        <div className="text-center p-4" style={{ background: "#fff", border: "1px solid #DDE5E0", borderRadius: 12, color: "#83958F" }}>
          {todos.length === 0
            ? <>Aún no hay constancias. Usa <b>＋ Nueva constancia</b> para registrar la primera.</>
            : "No hay constancias con estos filtros."}
        </div>
      ) : (
        <>
          {/* Escritorio / tableta: tabla */}
          <div className="d-none d-md-block" style={{ background: "#fff", border: "1px solid #DDE5E0", borderRadius: 12, overflowX: "auto" }}>
            <table>
              <thead>
                <tr>
                  <th>Fecha</th><th>Niño, niña o adolescente</th><th>Representante</th><th>Municipio / comunidad</th><th>Vacuna(s)</th><th>Estado</th><th style={{ textAlign: "right" }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtrados.map(r => (
                  <tr key={r._local ? `l-${r._localId}` : r.id} className={r._local ? "local" : ""}>
                    <td style={{ whiteSpace: "nowrap" }}>{formatearFecha(r.fecha_atencion)}</td>
                    <td><div style={{ fontWeight: 600 }}>{r.menor_nombre}</div><div className="sub">{formatearEdad(r)}</div></td>
                    <td><div style={{ fontWeight: 600 }}>{r.representante_nombre}</div><div className="sub">{r.representante_vinculo || ""}</div></td>
                    <td><div>{r.municipio}</div><div className="sub">{r.comunidad || ""}</div></td>
                    <td style={{ maxWidth: 220, fontSize: 12.5 }}>
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{r.vacunas_ofrecidas || "—"}</div>
                    </td>
                    <td><EstadoPill r={r} /></td>
                    <td><Acciones r={r} esAdmin={esAdmin} ocupado={ocupadoId === r.id} online={online}
                      onVer={() => setDetalle(r)} onFicha={() => generarFicha(r)} onEliminar={() => eliminar(r)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Celular: tarjetas */}
          <div className="d-md-none">
            {filtrados.map(r => (
              <div key={r._local ? `l-${r._localId}` : r.id} className={`dis-card ${r._local ? "local" : ""}`}>
                <div className="d-flex justify-content-between align-items-start gap-2">
                  <div>
                    <div style={{ fontWeight: 700 }}>{r.menor_nombre}</div>
                    <div className="sub">{formatearEdad(r)} — {r.municipio}{r.comunidad ? `, ${r.comunidad}` : ""}</div>
                  </div>
                  <EstadoPill r={r} />
                </div>
                <div style={{ fontSize: 13, marginTop: 6 }}>
                  <span className="sub">Representante: </span>{r.representante_nombre}
                </div>
                <div className="sub" style={{ marginTop: 2 }}>Atención: {formatearFecha(r.fecha_atencion)}{r.hora_atencion ? `, ${r.hora_atencion}` : ""}</div>
                <div className="mt-2">
                  <Acciones r={r} esAdmin={esAdmin} ocupado={ocupadoId === r.id} online={online} alinear="start"
                    onVer={() => setDetalle(r)} onFicha={() => generarFicha(r)} onEliminar={() => eliminar(r)} />
                </div>
              </div>
            ))}
          </div>
          <div className="sub mt-2">Mostrando {filtrados.length} de {todos.length}</div>
        </>
      )}

      <DisentimientoModal show={mostrarFormulario} onHide={() => setMostrarFormulario(false)} onGuardado={cargar} />
      {detalle && <DetalleModal registro={detalle} onClose={() => setDetalle(null)} />}
    </div>
  );
}

function EstadoPill({ r }) {
  const est = estadoFicha(r);
  return <span className="dis-pill" style={{ background: est.bg, color: est.color }} title={r._local && r.ultimoError ? r.ultimoError : undefined}>{est.texto}</span>;
}

function Acciones({ r, esAdmin, ocupado, online, onVer, onFicha, onEliminar, alinear = "end" }) {
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: alinear === "end" ? "flex-end" : "flex-start" }}>
      <Button size="sm" variant="outline-secondary" onClick={onVer} style={{ fontSize: 12 }}>Ver</Button>
      {!r._local && (
        <>
          <Button size="sm" disabled={ocupado || !online} onClick={onFicha} title={online ? "" : "Necesita conexión"}
            style={{ background: "#0E4F57", border: "none", fontSize: 12 }}>
            {ocupado ? "..." : (r.ficha_url ? "Reenviar ficha" : "Generar ficha")}
          </Button>
          {r.ficha_url && (
            <Button size="sm" variant="outline-secondary" as="a" href={urlFichaDisentimiento(r.ficha_url)} target="_blank" rel="noreferrer"
              style={{ fontSize: 12 }} disabled={!online}>
              PDF
            </Button>
          )}
          {esAdmin && (
            <Button size="sm" variant="outline-danger" disabled={ocupado || !online} onClick={onEliminar} style={{ fontSize: 12 }}>
              Eliminar
            </Button>
          )}
        </>
      )}
    </div>
  );
}

// Detalle completo. Los locales ya traen todo (incluida la firma); los del
// servidor se piden al servidor para tener la firma, y sin señal se
// muestra lo que hay en la lista guardada.
function DetalleModal({ registro, onClose }) {
  const [reg, setReg] = useState(registro._local ? registro : null);
  const [sinFirmaPorConexion, setSinFirmaPorConexion] = useState(false);

  useEffect(() => {
    if (registro._local) return;
    let vivo = true;
    getDisentimientoBac(registro.id)
      .then(res => { if (vivo) setReg(res.data); })
      .catch(() => { if (vivo) { setReg(registro); setSinFirmaPorConexion(true); } });
    return () => { vivo = false; };
  }, [registro]);

  return (
    <Modal show onHide={onClose} centered scrollable size="lg" fullscreen="sm-down" contentClassName="dis-modal-content">
      <Modal.Header closeButton style={{ background: "#0E4F57", color: "#fff" }}>
        <Modal.Title style={{ fontSize: "1rem" }}>Constancia {registro.codigo}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ fontSize: 13.5 }}>
        {!reg && <div className="text-center p-4"><Spinner /></div>}
        {reg && (
          <>
            {registro._local && (
              <div style={{ background: registro.sincronizado === 3 ? "#FCEBEB" : "#FFF4D6", color: registro.sincronizado === 3 ? "#A32D2D" : "#8A6100", borderRadius: 10, padding: "8px 12px", marginBottom: 14, fontSize: 13 }}>
                {registro.sincronizado === 3
                  ? <>⚠️ No se pudo subir: {registro.ultimoError || "error desconocido"}. Revísalo en el panel de pendientes de BAC.</>
                  : <>☁️ Guardada en este dispositivo — se enviará sola cuando haya señal.</>}
              </div>
            )}
            <Bloque titulo="Datos de atención">
              <Dato label="Municipio" valor={reg.municipio} />
              <Dato label="Comunidad o barrio" valor={reg.comunidad} />
              <Dato label="Fecha" valor={formatearFecha(reg.fecha_atencion)} />
              <Dato label="Hora" valor={reg.hora_atencion} />
            </Bloque>
            <Bloque titulo="Niño, niña o adolescente">
              <Dato label="Nombre" valor={reg.menor_nombre} />
              <Dato label="Documento" valor={reg.menor_documento} />
              <Dato label="Edad" valor={formatearEdad(reg)} />
            </Bloque>
            <Bloque titulo="Madre, padre o representante legal">
              <Dato label="Nombre" valor={reg.representante_nombre} />
              <Dato label="Documento" valor={reg.representante_documento} />
              <Dato label="Vínculo" valor={reg.representante_vinculo} />
              <Dato label="Teléfono" valor={reg.representante_telefono} />
              <Dato label="Correo" valor={reg.representante_correo} />
            </Bloque>
            <Bloque titulo="Vacunación ofrecida">
              <p style={{ whiteSpace: "pre-wrap", margin: 0 }}>{reg.vacunas_ofrecidas || "—"}</p>
            </Bloque>
            <Bloque titulo="Motivo de la decisión">
              <p style={{ whiteSpace: "pre-wrap", margin: 0 }}>{reg.motivo_decision || "—"}</p>
            </Bloque>
            <Bloque titulo="Constancia y firmas">
              <Dato label="Profesional o vacunador" valor={reg.profesional_nombre} />
              <Dato label="Cargo" valor={reg.profesional_cargo} />
              {reg.firma_base64 ? (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 12, color: "#83958F", marginBottom: 4 }}>Firma del representante</div>
                  <img src={reg.firma_base64} alt="Firma del representante" style={{ maxWidth: 280, width: "100%", border: "1px solid #DDE5E0", borderRadius: 10, background: "#fff" }} />
                </div>
              ) : reg.testigo_nombre ? (
                <>
                  <Dato label="Firma" valor="No firmó" />
                  <Dato label="¿Se ofreció lectura y firma?" valor={reg.se_ofrecio_lectura_firma === "Si" ? "Sí" : reg.se_ofrecio_lectura_firma} />
                  <Dato label="Testigo" valor={reg.testigo_nombre} />
                </>
              ) : sinFirmaPorConexion ? (
                <p className="sub mt-2 mb-0" style={{ color: "#83958F" }}>La firma se ve con conexión a internet.</p>
              ) : null}
            </Bloque>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}

function Bloque({ titulo, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: "#0E4F57", background: "#E4F5F8", borderRadius: 6, padding: "5px 11px", marginBottom: 8 }}>{titulo}</div>
      {children}
    </div>
  );
}
function Dato({ label, valor }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "5px 0", borderBottom: "1px solid #F1F3F2" }}>
      <span style={{ color: "#83958F", flexShrink: 0 }}>{label}</span>
      <span style={{ fontWeight: 600, textAlign: "right" }}>{valor || "—"}</span>
    </div>
  );
}
